import {
  X,
  MessageSquare,
  Tag,
  Star,
  Type,
  FileText,
  Paperclip,
  Image,
  Video,
  Music,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const CATEGORIES = [
  "Geral",
  "Saudação",
  "Despedida",
  "Vendas",
  "Suporte",
  "Agradecimento",
  "Confirmação",
  "Promoção",
];

const MESSAGE_TYPES = [
  { value: "text", label: "Texto" },
  { value: "image", label: "Imagem" },
  { value: "document", label: "Documento" },
  { value: "audio", label: "Áudio" },
  { value: "video", label: "Vídeo" },
];

const QuickMessageModal = ({
  isOpen,
  onClose,
  onSave,
  quickMessage = null,
}) => {
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "Geral",
    type: "text",
    tags: "",
    isFavorite: false,
    file: null,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose();
    };

    if (isOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleOverlayClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (quickMessage) {
      setFormData({
        title: quickMessage.title || "",
        content: quickMessage.content || "",
        category: quickMessage.category || "Geral",
        type: quickMessage.type || "text",
        tags: Array.isArray(quickMessage.tags)
          ? quickMessage.tags.join(", ")
          : "",
        isFavorite: quickMessage.isFavorite || false,
        file: null,
      });
    } else {
      setFormData({
        title: "",
        content: "",
        category: "Geral",
        type: "text",
        tags: "",
        isFavorite: false,
        file: null,
      });
    }
    setErrors({});
  }, [quickMessage, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Título é obrigatório";
    if (!formData.content.trim()) newErrors.content = "Conteúdo é obrigatório";
    if (!formData.category) newErrors.category = "Categoria é obrigatória";
    if (!formData.type) newErrors.type = "Tipo é obrigatório";

    if (formData.type !== "text" && !formData.file) {
      newErrors.file = "O arquivo é obrigatório para este tipo de mensagem";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, file }));
    if (errors.file) {
      setErrors((prev) => ({ ...prev, file: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const messageData = new FormData();

      messageData.append("title", formData.title);
      messageData.append("content", formData.content);
      messageData.append("category", formData.category);
      messageData.append("type", formData.type);
      messageData.append("isFavorite", formData.isFavorite);

      formData.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
        .forEach((tag) => messageData.append("tags", tag));

      if (formData.file) {
        messageData.append("file", formData.file);
      }

      if (quickMessage?.id) {
        messageData.append("id", quickMessage.id);
      }

      onSave(messageData);
      onClose();
    }
  };

  if (!isOpen) return null;

  const getAcceptedTypes = (type) => {
    switch (type) {
      case "image":
        return "image/*";
      case "document":
        return ".pdf,.doc,.docx,.txt";
      case "audio":
        return "audio/*";
      case "video":
        return "video/*";
      default:
        return "*/*";
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "image":
        return <Image className="mr-2 h-4 w-4" />;
      case "audio":
        return <Music className="mr-2 h-4 w-4" />;
      case "video":
        return <Video className="mr-2 h-4 w-4" />;
      default:
        return <Paperclip className="mr-2 h-4 w-4" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        >
          <motion.div
            ref={modalRef}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-gray-900"
          >
            {/* Cabeçalho */}
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <h2 className="flex items-center text-xl font-semibold text-gray-900 dark:text-gray-100">
                <MessageSquare className="mr-2 h-5 w-5" />
                {quickMessage
                  ? "Editar Mensagem Rápida"
                  : "Nova Mensagem Rápida"}
              </h2>
              <button
                onClick={onClose}
                className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-5">
                {/* Título */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Título *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.title
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                    } bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="Digite o título da mensagem"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                  )}
                </div>

                {/* Conteúdo fixo */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <FileText className="mr-2 h-4 w-4" />
                    Conteúdo *
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    rows="6"
                    className={`w-full rounded-lg border ${
                      errors.content
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                    } bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="Digite o conteúdo da mensagem"
                  />
                  {errors.content && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.content}
                    </p>
                  )}
                </div>

                {/* Categoria e Tipo */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Tag className="mr-2 h-4 w-4" />
                      Categoria *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={`w-full rounded-lg border ${
                        errors.category
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                      } bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                    >
                      {CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.category}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Type className="mr-2 h-4 w-4" />
                      Tipo *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className={`w-full rounded-lg border ${
                        errors.type
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                      } bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                    >
                      {MESSAGE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-500">{errors.type}</p>
                    )}
                  </div>
                </div>

                {/* Input de arquivo abaixo do select */}
                {formData.type !== "text" && (
                  <div>
                    <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      {getFileIcon(formData.type)}
                      Arquivo (
                      {
                        MESSAGE_TYPES.find((t) => t.value === formData.type)
                          ?.label
                      }
                      ) *
                    </label>
                    <input
                      type="file"
                      accept={getAcceptedTypes(formData.type)}
                      onChange={handleFileChange}
                      className={`w-full rounded-lg border ${
                        errors.file
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                      } bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                    />
                    {formData.file && (
                      <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                        Arquivo selecionado:{" "}
                        <strong>{formData.file.name}</strong>
                      </p>
                    )}
                    {errors.file && (
                      <p className="mt-1 text-sm text-red-500">{errors.file}</p>
                    )}
                  </div>
                )}

                {/* Tags */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Tag className="mr-2 h-4 w-4" />
                    Tags (separadas por vírgula)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 transition-colors focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    placeholder="urgente, importante, promoção"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Separe as tags com vírgulas
                  </p>
                </div>

                {/* Favorito */}
                <div className="flex items-center space-x-3 rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800">
                  <input
                    type="checkbox"
                    name="isFavorite"
                    id="isFavorite"
                    checked={formData.isFavorite}
                    onChange={handleChange}
                    className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                  <label
                    htmlFor="isFavorite"
                    className="flex cursor-pointer items-center text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    <Star className="mr-2 h-4 w-4 text-yellow-500" />
                    Marcar como favorita
                  </label>
                </div>

                {/* Preview */}
                <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                  <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pré-visualização:
                  </span>
                  <div className="space-y-2 rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {formData.title || "Título da Mensagem"}
                          </p>
                          {formData.isFavorite && (
                            <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                          )}
                        </div>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                          {formData.content ||
                            "Conteúdo da mensagem aparecerá aqui..."}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-2 dark:border-gray-600">
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {formData.category}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                        {MESSAGE_TYPES.find((t) => t.value === formData.type)
                          ?.label || "Texto"}
                      </span>
                      {formData.tags &&
                        formData.tags
                          .split(",")
                          .map((tag) => tag.trim())
                          .filter((tag) => tag.length > 0)
                          .map((tag, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300"
                            >
                              {tag}
                            </span>
                          ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Botões */}
              <div className="mt-6 flex justify-end space-x-3 border-t border-gray-200 pt-6 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600"
                >
                  {quickMessage ? "Salvar Alterações" : "Criar Mensagem"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuickMessageModal;
